import { useQueryClient } from "@tanstack/react-query";
import FetchClients, { ClientOutput } from "../client";
import { PROJECT_KEY, useInvalidateProjectsPortfolioSummary } from "./project";
import { PORTFOLIO_REBATES_QUERY_KEY } from "../rebates/rebates";
import attachments, { ATTACHMENT_PREVIEW_BASE_KEY } from "./rewardAttachments";
import {
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";

const rewards = {
  useQueryAllVendors,
  attachments,
  mutations: {
    usePost,
    usePatch,
    useDelete,
  },
} as const;

export type Reward = ClientOutput["projects"]["Reward"];
type RewardCreate = ClientOutput["projects"]["RewardCreate"];
type RewardEdit = ClientOutput["projects"]["RewardEdit"] & {
  id: number;
};
export type RewardVendor = ClientOutput["projects"]["RewardVendor"];

// endpoint requires {feature: "Projects", access: "read"}
function useQueryAllVendors() {
  return useQueryWithAccessCheckLog(
    ["Projects", "read"],
    async (checkFeatureAccessHeader) => {
      const { data, error } = await FetchClients.projects.GET(
        "/rewards/vendors",
        {
          headers: checkFeatureAccessHeader,
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    {
      queryKey: ["all-vendors"],
    }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
function usePost(project_id: number, site_id: number) {
  const queryClient = useQueryClient();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async (body: RewardCreate, checkFeatureAccessHeader) => {
      const { data, error } = await FetchClients.projects.POST(
        "/{project_id}/rewards",
        {
          params: {
            path: { project_id },
          },
          body,
          headers: checkFeatureAccessHeader,
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    {
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [PROJECT_KEY, site_id],
          }),
          queryClient.invalidateQueries({
            queryKey: [ATTACHMENT_PREVIEW_BASE_KEY, "rewards", project_id],
          }),
          invalidatePortfolioSummary(),
          // a reward is the project-side view of a rebate, so keep the portfolio
          // rebates list and its summary cards in step
          queryClient.invalidateQueries({
            queryKey: [PORTFOLIO_REBATES_QUERY_KEY],
          }),
        ]),

      onError: (e) => {
        throw new Error(
          `Unable to create new reward for project ${project_id}, site ${site_id}
        ${(e as Error)?.message || "Unknown Error"}`
        );
      },
    }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
function usePatch(project_id: number, site_id: number) {
  const queryClient = useQueryClient();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async (_body: RewardEdit, checkFeatureAccessHeader) => {
      const { id, ...body } = _body;
      const { data, error } = await FetchClients.projects.PATCH(
        "/{project_id}/rewards/{reward_id}",
        {
          params: {
            path: { project_id, reward_id: id },
          },
          body,
          headers: checkFeatureAccessHeader,
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    {
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [PROJECT_KEY, site_id],
          }),
          queryClient.invalidateQueries({
            queryKey: [ATTACHMENT_PREVIEW_BASE_KEY, "rewards", project_id],
          }),
          invalidatePortfolioSummary(),
          // a reward is the project-side view of a rebate, so keep the portfolio
          // rebates list and its summary cards in step
          queryClient.invalidateQueries({
            queryKey: [PORTFOLIO_REBATES_QUERY_KEY],
          }),
        ]),
      onError: (e) => {
        throw new Error(
          `Unable to update reward for project ${project_id}, site ${site_id}
        ${(e as Error)?.message || "Unknown Error"}`
        );
      },
    }
  );
}

// endpoint requires {feature: "Projects", access: "edit"}
function useDelete(project_id: number, site_id: number) {
  const queryClient = useQueryClient();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  return useMutationWithAccessCheckLog(
    ["Projects", "edit"],
    async (id: number, checkFeatureAccessHeader) => {
      const { data, error } = await FetchClients.projects.DELETE(
        "/{project_id}/rewards/{reward_id}",
        {
          params: {
            path: { project_id, reward_id: id },
          },
          headers: checkFeatureAccessHeader,
        }
      );
      if (data) return data;
      throw new Error(error);
    },
    {
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({
            queryKey: [PROJECT_KEY, site_id],
          }),
          queryClient.invalidateQueries({
            queryKey: [ATTACHMENT_PREVIEW_BASE_KEY, "rewards", project_id],
          }),
          invalidatePortfolioSummary(),
          // a reward is the project-side view of a rebate, so keep the portfolio
          // rebates list and its summary cards in step
          queryClient.invalidateQueries({
            queryKey: [PORTFOLIO_REBATES_QUERY_KEY],
          }),
        ]),
      onError: (e) => {
        throw new Error(
          `Unable to delete reward for project ${project_id}, site ${site_id}
        ${(e as Error)?.message || "Unknown Error"}`
        );
      },
    }
  );
}
export default rewards;
